import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { Description, MainHeadline } from 'components/Typography'
import { NavButton, OrangeButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCard, StoreDeliveryFeeConfirmCardList } from 'components/Card'
import { State } from 'index'
import { BreadType } from 'types/bread'
import { getBreads } from 'reducks/bread/selectors'
import { StockType } from 'types/stock'
import { getStocks } from 'reducks/stock/selectors'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchBreads } from 'reducks/bread/operations'
import { fetchFutureStocks, initStocks, unsubscribeStocks } from 'reducks/stock/operations'
import { useFetchDeliveryFees } from 'hooks/storeDeliveryFee'
import { saveStoreDeliveryFees } from 'reducks/storeDeliveryFee/operations'
import { StoreDeliveryBreadType } from 'types/storeDeliveryBread'
import { useFetchStoreDeliveryBreads } from 'hooks/storeDeliveryBread'
import { PackageSize } from 'types/storeDeliveryFee'

const StoreDeliveryFeeConfirmTemplate: FC = () => {
  const dispatch = useDispatch()

  const breads: BreadType[] = useSelector((state: State) => getBreads(state))
  const stocks: StockType[] = useSelector((state: State) => getStocks(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeDeliveryBreads: StoreDeliveryBreadType[] = useFetchStoreDeliveryBreads()
  const storeDeliveryFees = useFetchDeliveryFees()

  // 未設定の配送サイズが指定されている商品を絞り込む
  const breadList: BreadType[] = useMemo(() => {
    if (breads.length === 0 || storeDeliveryBreads.length === 0) return []
    const packageSizes: PackageSize[] = storeDeliveryFees.map((storeDeliveryFee) => storeDeliveryFee.package_size)
    return breads.filter((bread) => {
      const storeDeliveryBread = storeDeliveryBreads.find((_) => _.bread_ref?.id === bread.bread_ref?.id)
      if (!storeDeliveryBread) return false
      return !packageSizes.includes(storeDeliveryBread.package_size)
    })
  }, [breads, storeDeliveryBreads, storeDeliveryFees])

  const saveDeliveryFees = useCallback(() => {
    dispatch(saveStoreDeliveryFees(breadList, stocks, storeDeliveryFees, storeRef))
  }, [breadList, stocks, storeDeliveryFees, storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(initStocks())
    dispatch(fetchFutureStocks({ storeRef, types: ['delivery'] }))
    return () => {
      dispatch(unsubscribeStocks())
    }
  }, [storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'public'))
  }, [storeRef])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'修正内容の確認'} />
      <Spacer size={20} />
      {breadList.length > 0 ? (
        <>
          <AlertCard
            description={'変更される箇所は赤字で表示しておりますので、必ずご確認いただき、保存を行ってください。'}
            headline={'すでに登録されている商品の内容が変更されます。'}
            title={'注意'}
          />
          <Spacer size={16} />
          <OrangeButton label={'保存する'} onClick={saveDeliveryFees} size={'medium'} type={'main'} />
          <Spacer size={48} />
          <StoreDeliveryFeeConfirmCardList
            breadList={breadList}
            stocks={stocks}
            storeDeliveryBreads={storeDeliveryBreads}
          />
        </>
      ) : (
        <Description description={'すでに登録されている商品の内容に変更はありません。'} />
      )}
      <Spacer size={48} />
      <OrangeButton label={'保存する'} onClick={saveDeliveryFees} size={'medium'} type={'main'} />
      <Spacer size={20} />
      <NavButton label={'配送設定へ戻る'} iconPath={'/icon/grey/arrow_prev.svg'} url={'/delivery/setting/fee'} />
      <Spacer size={72} />
    </div>
  )
}

export default StoreDeliveryFeeConfirmTemplate
